import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { fetchStatistics, type MonthlyTrend } from '@/services/statistics';

function CustomTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-background-100 border border-background-200/70 rounded-md px-3 py-1.5 shadow-sm">
      <p className="text-xs font-semibold text-foreground-900">{label}</p>
      {payload.map((entry, idx) => (
        <p key={idx} className="text-[11px]" style={{ color: entry.color }}>
          {entry.name}: {entry.value}%
        </p>
      ))}
    </div>
  );
}

export default function MonthlyTrendWidget() {
  const [monthlyTrends, setMonthlyTrends] = useState<MonthlyTrend[] | null>(null);

  useEffect(() => {
    let mounted = true;
    fetchStatistics().then((stats) => {
      if (mounted && stats) setMonthlyTrends(stats.monthlyTrends);
    });
    return () => { mounted = false; };
  }, []);

  if (!monthlyTrends) {
    return (
      <div className="bg-background-100 rounded-lg border border-background-200/60 overflow-hidden animate-pulse">
        <div className="px-4 py-3 border-b border-background-200/60">
          <div className="h-5 w-32 rounded bg-background-200" />
        </div>
        <div className="p-4"><div className="h-[160px] rounded bg-background-200/50" /></div>
      </div>
    );
  }

  const withData = monthlyTrends.filter((m) => m.completionRate > 0);
  const highest = withData.length ? withData.reduce((a, b) => (b.completionRate >= a.completionRate ? b : a)) : null;
  const lowest = withData.length ? withData.reduce((a, b) => (b.completionRate < a.completionRate ? b : a)) : null;
  const avg = withData.length
    ? (withData.reduce((s, m) => s + m.completionRate, 0) / withData.length).toFixed(1)
    : '0';

  return (
    <div className="bg-background-100 rounded-lg border border-background-200/60 overflow-hidden">
      <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
        <span className="w-6 h-6 flex items-center justify-center">
          <i className="ri-line-chart-line text-accent-500 text-lg" />
        </span>
        <h3 className="text-sm font-heading font-semibold text-foreground-950">Monthly Trend</h3>
        <span className="text-[10px] text-foreground-400 font-mono ml-auto">{new Date().getFullYear()}</span>
      </div>

      <div className="p-4">
        <div className="h-[160px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={monthlyTrends} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="completionGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-200) / 0.6)" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'oklch(var(--foreground-400))' }} axisLine={false} tickLine={false} />
              <YAxis domain={[60, 100]} tick={{ fontSize: 10, fill: 'oklch(var(--foreground-400))' }} axisLine={false} tickLine={false} width={28} tickFormatter={(v: number) => `${v}%`} />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="completionRate"
                name="Completion"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#completionGradient)"
                dot={{ fill: '#10b981', strokeWidth: 0, r: 3 }}
                activeDot={{ fill: '#10b981', strokeWidth: 0, r: 5 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Stats below */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/60">
          <div className="text-center flex-1">
            <p className="text-[10px] text-foreground-400">Highest</p>
            <p className="text-sm font-semibold text-foreground-900 font-mono">{highest ? `${highest.completionRate}%` : '—'}</p>
            <p className="text-[10px] text-foreground-400">{highest?.month || ''}</p>
          </div>
          <div className="w-px h-8 bg-background-200/60" />
          <div className="text-center flex-1">
            <p className="text-[10px] text-foreground-400">Lowest</p>
            <p className="text-sm font-semibold text-foreground-900 font-mono">{lowest ? `${lowest.completionRate}%` : '—'}</p>
            <p className="text-[10px] text-foreground-400">{lowest?.month || ''}</p>
          </div>
          <div className="w-px h-8 bg-background-200/60" />
          <div className="text-center flex-1">
            <p className="text-[10px] text-foreground-400">Avg</p>
            <p className="text-sm font-semibold text-foreground-900 font-mono">{avg}%</p>
            <p className="text-[10px] text-foreground-400">last 6 mo</p>
          </div>
        </div>
      </div>
    </div>
  );
}